import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wordmark } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { ArrowRight, ArrowLeft } from "lucide-react";

function Choice({ active, title, body, onClick, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={cn(
                "w-full rounded-2xl border p-5 text-left transition-all duration-150",
                active ? "border-nm-accent bg-nm-accent-soft ring-1 ring-nm-accent" : "border-nm-line bg-nm-surface hover:border-nm-accent/50"
            )}
        >
            <p className="font-semibold text-nm-ink">{title}</p>
            <p className="mt-1 text-sm text-nm-muted">{body}</p>
        </button>
    );
}

export default function Onboarding() {
    const { user, updateUser } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [data, setData] = useState({
        starting_point: user?.starting_point || null,
        goal: user?.goal || null,
        company: user?.company || "",
        story: user?.story || "",
    });
    const [saving, setSaving] = useState(false);

    const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

    const finish = async () => {
        setSaving(true);
        try {
            const { data: res } = await api.put("/auth/profile", {
                starting_point: data.starting_point,
                goal: data.goal,
                company: data.company?.trim() || null,
                story: data.story?.trim() || null,
                onboarding_completed: true,
            });
            updateUser(res.user);
            toast.success("¡Tu ruta está lista!");
            navigate("/app");
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-nm-bg">
            <header className="border-b border-nm-line bg-nm-surface">
                <div className="mx-auto flex max-w-2xl items-center justify-between px-6 py-4">
                    <Wordmark />
                    <span className="text-sm text-nm-muted tabular-nums">Paso {step} de 3</span>
                </div>
            </header>

            <div className="mx-auto max-w-2xl px-6 py-12">
                <div className="mb-8 h-1.5 w-full overflow-hidden rounded-full bg-nm-line">
                    <div className="h-full rounded-full bg-nm-accent transition-all duration-300" style={{ width: `${(step / 3) * 100}%` }} />
                </div>

                {step === 1 && (
                    <div className="animate-fade-up" data-testid="onboarding-step-1">
                        <h1 className="text-3xl font-bold tracking-tight text-nm-ink">¿Desde dónde empiezas?</h1>
                        <p className="mt-2 text-nm-muted">Ajustamos tu ruta a tu momento actual.</p>
                        <div className="mt-8 space-y-3">
                            <Choice active={data.starting_point === "desde_cero"} onClick={() => set("starting_point", "desde_cero")}
                                testId="onboarding-start-zero-option"
                                title="Empiezo desde cero" body="No tengo contactos ni experiencia todavía. Quiero aprender el sistema." />
                            <Choice active={data.starting_point === "networker"} onClick={() => set("starting_point", "networker")}
                                testId="onboarding-start-networker-option"
                                title="Ya soy networker" body="Tengo algo de experiencia o equipo, pero me falta constancia." />
                        </div>
                        <div className="mt-10 flex justify-end">
                            <Button disabled={!data.starting_point} onClick={() => setStep(2)} data-testid="onboarding-next-1">
                                Continuar <ArrowRight className="ml-1 h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="animate-fade-up" data-testid="onboarding-step-2">
                        <h1 className="text-3xl font-bold tracking-tight text-nm-ink">¿Qué quieres conseguir?</h1>
                        <p className="mt-2 text-nm-muted">Puedes cambiarlo cuando quieras desde Ajustes.</p>
                        <div className="mt-8 space-y-3">
                            <Choice active={data.goal === "clientes"} onClick={() => set("goal", "clientes")}
                                testId="onboarding-goal-clientes"
                                title="Captar clientes" body="Vender el producto y conseguir compradores contentos." />
                            <Choice active={data.goal === "equipo"} onClick={() => set("goal", "equipo")}
                                testId="onboarding-goal-equipo"
                                title="Construir equipo" body="Incorporar personas y hacer crecer mi organización." />
                            <Choice active={data.goal === "ambos"} onClick={() => set("goal", "ambos")}
                                testId="onboarding-goal-ambos"
                                title="Las dos cosas" body="Quiero clientes y equipo a la vez." />
                        </div>
                        <div className="mt-10 flex justify-between">
                            <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="mr-1 h-4 w-4" /> Atrás</Button>
                            <Button disabled={!data.goal} onClick={() => setStep(3)} data-testid="onboarding-next-2">
                                Continuar <ArrowRight className="ml-1 h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="animate-fade-up" data-testid="onboarding-step-3">
                        <h1 className="text-3xl font-bold tracking-tight text-nm-ink">Un par de datos y listo</h1>
                        <p className="mt-2 text-nm-muted">Esto es opcional, pero ayuda a personalizar tus mensajes.</p>
                        <div className="mt-8 space-y-4">
                            <div className="space-y-1.5">
                                <Label htmlFor="company">¿Con qué empresa o producto trabajas?</Label>
                                <Input id="company" data-testid="onboarding-company" value={data.company}
                                    onChange={(e) => set("company", e.target.value)} placeholder="Opcional" />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="story">Tu historia en una frase</Label>
                                <Textarea id="story" data-testid="onboarding-story" rows={3} value={data.story}
                                    onChange={(e) => set("story", e.target.value)}
                                    placeholder="Por qué empezaste y qué buscas. Opcional." />
                            </div>
                        </div>
                        <div className="mt-10 flex justify-between">
                            <Button variant="outline" onClick={() => setStep(2)}><ArrowLeft className="mr-1 h-4 w-4" /> Atrás</Button>
                            <Button onClick={finish} disabled={saving} data-testid="onboarding-finish">
                                {saving ? "Preparando tu ruta..." : "Empezar mi ruta"}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
